import React from 'react';
import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TOUR_STEPS } from './legislativeTour';

const DIM_SOFT = 'rgba(15, 23, 42, 0.28)';
const DIM_STRONG = 'rgba(15, 23, 42, 0.62)';
const SPOTLIGHT_GREEN = '#39FF14';
const cutoutRing = (dim) =>
  `0 0 0 3px ${SPOTLIGHT_GREEN}, 0 0 16px ${SPOTLIGHT_GREEN}, 0 0 0 9999px ${dim}`;

const regionSpotlightSx = {
  'map-desert': {
    // Sit in the SW/SE colored quadrants, below the city core, so the
    // window is inside a desert rather than on the urban fringe.
    left: { xs: '10%', sm: '16%' },
    top: { xs: '52%', sm: '56%' },
    width: { xs: '32%', sm: '24%' },
    height: { xs: '30%', sm: '28%' },
  },
  'map-covered': {
    left: '50%',
    top: '46%',
    transform: 'translate(-50%, -50%)',
    width: { xs: 200, sm: 280 },
    height: { xs: 160, sm: 200 },
  },
  'map-zoom': {
    left: 8,
    top: 8,
    width: 52,
    height: 108,
  },
  'map-schools-east': {
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: 240, sm: 380 },
    height: { xs: 220, sm: 300 },
  },
  'map-schools-west': {
    left: { xs: 24, sm: 340 },
    top: { xs: '46%', sm: '48%' },
    width: { xs: 210, sm: 280 },
    height: { xs: 160, sm: 220 },
  },
  'example-popup': {
    left: '50%',
    top: '58%',
    transform: 'translate(-50%, -50%)',
    width: 296,
    height: 248,
  },
  'legend-box': {
    left: 8,
    right: 8,
    bottom: 0,
    top: 'auto',
    width: 'auto',
    height: { xs: 110, sm: 140 },
  },
  'map-search': {
    left: '50%',
    top: 8,
    transform: 'translateX(-50%)',
    width: { xs: 280, sm: 560 },
    height: 56,
  },
  'sector-toggle': {
    left: '50%',
    top: 8,
    transform: 'translateX(-50%)',
    width: { xs: 260, sm: 320 },
    height: 48,
  },
};

const StatTile = ({ label, value, valueColor = '#111' }) => (
  <Box sx={{ bgcolor: '#f7f8fa', borderRadius: '6px', px: '9px', py: '7px' }}>
    <Box sx={{ fontSize: 10, color: '#999', mb: '2px' }}>{label}</Box>
    <Box sx={{ fontSize: 12, fontWeight: 700, color: valueColor, lineHeight: 1.3 }}>{value}</Box>
  </Box>
);

const ExampleSchoolPopup = ({ variant = 'default' }) => {
  const isQuality = variant === 'quality';
  const accent = isQuality ? '#2a9d2a' : '#1a6fb5';

  return (
  <Box
    data-testid="tour-example-school-popup"
    sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      pt: 0.75,
      pointerEvents: 'none',
    }}
  >
    <Box
      sx={{
        width: 280,
        bgcolor: '#ffffff',
        borderRadius: '8px',
        overflow: 'hidden',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        boxShadow: '0 2px 10px rgba(0,0,0,0.22)',
      }}
    >
      <Box sx={{ bgcolor: accent, height: '4px' }} />
      <Box sx={{ px: '14px', pt: '12px', pb: '14px' }}>
        <Box sx={{ fontSize: 15, fontWeight: 700, color: '#111', lineHeight: 1.3, mb: '3px' }}>
          Example Charter School
        </Box>
        <Box sx={{ fontSize: 11, color: '#999', mb: '9px' }}>123 Sample Street</Box>
        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mb: '10px' }}>
          <Box
            sx={{
              fontSize: 10,
              fontWeight: 600,
              color: '#fff',
              bgcolor: '#1a6fb5',
              borderRadius: '3px',
              px: '7px',
              py: '2px',
              textTransform: 'uppercase',
            }}
          >
            Charter
          </Box>
          <Box sx={{ fontSize: 10, color: '#666', bgcolor: '#f0f0f0', borderRadius: '3px', px: '7px', py: '2px' }}>
            Senate District 15
          </Box>
          <Box sx={{ fontSize: 10, color: '#666', bgcolor: '#f0f0f0', borderRadius: '3px', px: '7px', py: '2px' }}>
            House District 25
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', mb: isQuality ? '6px' : '8px' }}>
          <Box sx={{ bgcolor: '#f7f8fa', borderRadius: '6px', px: '9px', py: '7px' }}>
            <Box sx={{ fontSize: 10, color: '#999', mb: '2px' }}>Enrollment 2025-26</Box>
            <Box sx={{ fontSize: 15, fontWeight: 700, color: '#111' }}>366</Box>
          </Box>
          <Box sx={{ bgcolor: '#f7f8fa', borderRadius: '6px', px: '9px', py: '7px' }}>
            <Box sx={{ fontSize: 10, color: '#999', mb: '2px' }}>Enrollment Trend</Box>
            <Box sx={{ fontSize: 14, fontWeight: 700, color: '#666' }}>Stable</Box>
          </Box>
        </Box>
        {isQuality && (
          <Box
            data-testid="tour-example-quality-fields"
            sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', mb: '8px' }}
          >
            <StatTile label="Math Proficiency" value="At or Above City & State" valueColor="#2a9d2a" />
            <StatTile label="Reading Proficiency" value="At or Above City & State" valueColor="#2a9d2a" />
            <StatTile label="Math Growth" value="At or Above City & State" valueColor="#2a9d2a" />
            <StatTile label="Reading Growth" value="Below / mixed" valueColor="#cc2222" />
          </Box>
        )}
        <Box sx={{ fontSize: 10, color: '#888', lineHeight: 1.5 }}>
          <Box component="span" sx={{ fontWeight: 600, color: '#555' }}>
            Grades:{' '}
          </Box>
          K–8
        </Box>
      </Box>
    </Box>
    <Box
      sx={{
        width: 0,
        height: 0,
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderTop: '10px solid #ffffff',
        filter: 'drop-shadow(0 2px 1px rgba(0,0,0,0.12))',
        mt: '-1px',
      }}
    />
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        bgcolor: accent,
        border: '2.5px solid #ffffff',
        boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
        mt: 0.4,
      }}
    />
  </Box>
  );
};

const LegislativeMapTour = ({
  steps = TOUR_STEPS,
  step = 0,
  onStepChange,
  onDismiss,
  testId = 'legislative-map-tour',
}) => {
  const goToStep = onStepChange;
  const dismiss = onDismiss;
  const safeStep = Math.min(Math.max(step, 0), Math.max(steps.length - 1, 0));
  const current = steps[safeStep] || {};
  const isFirst = safeStep === 0;
  const isLast = safeStep === steps.length - 1;
  const spotlight = current.spotlight;
  const hasCutout = Boolean(spotlight);
  const dim = hasCutout ? DIM_STRONG : DIM_SOFT;
  const cardAlign = current.cardAlign;
  const cardBesideLegend = spotlight === 'legend-strip';
  const cardPositionSx =
    cardAlign === 'bottom-left'
      ? { bottom: 20, left: 20, top: 'auto' }
      : cardAlign === 'left'
        ? { top: 20, left: 20 }
        : cardBesideLegend
          ? { top: 20, left: { xs: 20, sm: 332 }, right: { xs: 20, sm: 'auto' } }
          : { top: 20, right: 20 };

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 3,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
      data-testid={testId}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: hasCutout ? 'transparent' : dim,
        }}
      />
      {spotlight === 'legend-strip' && (
        <Box
          aria-hidden
          data-testid={`${testId}-legend-spotlight`}
          sx={{
            position: 'absolute',
            top: 12,
            bottom: 12,
            left: 12,
            width: { xs: 208, sm: 288 },
            borderRadius: 2,
            boxShadow: cutoutRing(dim),
            pointerEvents: 'none',
          }}
        />
      )}
      {regionSpotlightSx[spotlight] && (
        <Box
          aria-hidden
          data-testid={`${testId}-region-spotlight`}
          sx={{
            position: 'absolute',
            ...regionSpotlightSx[spotlight],
            ...(spotlight === 'example-popup' && current.popupVariant === 'quality'
              ? { height: 380, top: '52%' }
              : {}),
            borderRadius: 2,
            boxShadow: cutoutRing(dim),
            pointerEvents: 'none',
            overflow: spotlight === 'example-popup' ? 'visible' : 'hidden',
          }}
        >
          {spotlight === 'example-popup' ? (
            <ExampleSchoolPopup variant={current.popupVariant || 'default'} />
          ) : null}
          {current.spotlightLabel && spotlight !== 'example-popup' && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                bgcolor: 'background.paper',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                boxShadow: 1,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {current.spotlightLabel}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Paper
        role="dialog"
        aria-labelledby={`${testId}-title`}
        aria-describedby={`${testId}-body`}
        elevation={8}
        sx={{
          pointerEvents: 'auto',
          position: 'absolute',
          ...cardPositionSx,
          width: { xs: 'calc(100% - 40px)', sm: 360 },
          maxWidth: 360,
          p: 2,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {safeStep + 1} of {steps.length}
            </Typography>
            <Typography id={`${testId}-title`} variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {current.title}
            </Typography>
          </Box>
          <IconButton size="small" onClick={dismiss} aria-label="Skip tour">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography id={`${testId}-body`} variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {current.body}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Button size="small" onClick={dismiss} sx={{ textTransform: 'none' }}>
            Skip
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              disabled={isFirst}
              onClick={() => goToStep(safeStep - 1)}
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={() => (isLast ? dismiss() : goToStep(safeStep + 1))}
              sx={{ textTransform: 'none' }}
            >
              {isLast ? 'Done' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default LegislativeMapTour;
